import { FLAG_BADGES, RESULT_BADGES } from '@parlay-picker/shared/badgeConfig';
import type { BoxScorePlayerRow, BoxScoreTeam, GameStatus } from '@parlay-picker/shared/hooks';
import type { ReactElement } from 'react';

export const STAT_COLUMNS = [
  'MIN',
  'PTS',
  'REB',
  'AST',
  'FG',
  '3PT',
  'FT',
  'STL',
  'BLK',
  'TO',
  'PF',
] as const;

export type StatColumn = (typeof STAT_COLUMNS)[number];

export function formatStat(player: BoxScorePlayerRow, col: StatColumn): string {
  switch (col) {
    case 'MIN':
      return player.min;
    case 'PTS':
      return String(player.pts);
    case 'REB':
      return String(player.reb);
    case 'AST':
      return String(player.ast);
    case 'FG':
      return `${player.fgm}-${player.fga}`;
    case '3PT':
      return `${player.fg3m}-${player.fg3a}`;
    case 'FT':
      return `${player.ftm}-${player.fta}`;
    case 'STL':
      return String(player.stl);
    case 'BLK':
      return String(player.blk);
    case 'TO':
      return String(player.turnover);
    case 'PF':
      return String(player.pf);
  }
}

/** Check if a player didn't play or has zero minutes */
export function isBenchOrDNP(min: string): boolean {
  if (!min) return true;
  const trimmed = min.trim();
  if (
    trimmed === 'DNP' ||
    trimmed === '0' ||
    trimmed === '00' ||
    trimmed === '00:00' ||
    trimmed === '0:00'
  )
    return true;
  // Parse numeric — if 0 or less, it's bench/DNP
  const parts = trimmed.split(':');
  if (parts.length === 2) {
    return parseInt(parts[0], 10) === 0 && parseInt(parts[1], 10) === 0;
  }
  const n = parseFloat(trimmed);
  return !Number.isNaN(n) && n <= 0;
}

function PlayerRow({
  player,
  showDNP = true,
  isOnCourt = false,
}: {
  player: BoxScorePlayerRow;
  showDNP?: boolean;
  isOnCourt?: boolean;
}): ReactElement {
  const isDNP = showDNP && isBenchOrDNP(player.min);

  return (
    <tr
      className={`border-b border-theme-border/20 ${
        player.isTracked
          ? 'bg-[var(--color-primary)]/8 border-l-2 border-l-[var(--color-primary)]'
          : ''
      } ${isDNP ? 'opacity-40' : ''}`}
    >
      <td className="text-left py-1 px-2 text-xs text-white whitespace-nowrap sticky left-0 bg-[var(--color-surface)] z-10">
        {isOnCourt && (
          <span className="inline-block w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse mr-1 align-middle" />
        )}
        <span className="truncate max-w-[120px] inline-block align-middle">
          {player.playerName}
        </span>
        {player.isTracked && (
          <span
            className={`ml-1 text-[8px] px-1 py-0.5 rounded font-medium align-middle ${FLAG_BADGES.isTracked.bg} ${FLAG_BADGES.isTracked.text}`}
          >
            {FLAG_BADGES.isTracked.label}
          </span>
        )}
        {isDNP && (
          <span
            className={`ml-1 text-[8px] px-1 py-0.5 rounded font-medium align-middle ${RESULT_BADGES.dnp.bg} ${RESULT_BADGES.dnp.text}`}
          >
            {RESULT_BADGES.dnp.label}
          </span>
        )}
      </td>
      {STAT_COLUMNS.map((col) => (
        <td
          key={col}
          className="text-center py-1 px-1.5 text-xs text-theme-text-secondary font-mono whitespace-nowrap"
        >
          {isDNP ? (col === 'MIN' ? 'DNP' : '-') : formatStat(player, col)}
        </td>
      ))}
    </tr>
  );
}

export function TeamBoxScore({
  team,
  isActive,
  gameState,
  onCourtMap,
}: {
  team: BoxScoreTeam;
  isActive: boolean;
  gameState: GameStatus;
  onCourtMap: Map<number, boolean>;
}): ReactElement | null {
  if (!isActive) return null;

  // Only split into played vs DNP when game is in progress or final.
  // Pre-game: everyone has zero minutes — don't label anyone as DNP.
  const canShowDNP = gameState === 'live' || gameState === 'final';
  const playedPlayers = canShowDNP
    ? team.players.filter((p) => !isBenchOrDNP(p.min))
    : team.players;
  const dnpPlayers = canShowDNP ? team.players.filter((p) => isBenchOrDNP(p.min)) : [];

  return (
    <div className="max-h-[40vh] overflow-y-auto overflow-x-auto pr-1">
      <table className="w-full text-xs min-w-[580px]">
        <thead className="sticky top-0 bg-[var(--color-surface)] z-20">
          <tr className="text-theme-text-tertiary border-b border-theme-border/30">
            <th className="text-left font-normal py-1 px-2 sticky left-0 bg-[var(--color-surface)] z-30 min-w-[130px]">
              Player
            </th>
            {STAT_COLUMNS.map((col) => (
              <th key={col} className="text-center font-normal py-1 px-1.5">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Players who have entered the game (sorted by minutes DESC) */}
          {playedPlayers.map((player) => (
            <PlayerRow
              key={player.playerId}
              player={player}
              showDNP={canShowDNP}
              isOnCourt={gameState === 'live' && onCourtMap.get(player.playerId) === true}
            />
          ))}

          {/* DNP divider */}
          {dnpPlayers.length > 0 && playedPlayers.length > 0 && (
            <tr>
              <td
                colSpan={STAT_COLUMNS.length + 1}
                className="py-1 px-2 text-[10px] font-medium text-theme-text-tertiary uppercase tracking-wider bg-theme-surface-hover/30 border-y border-theme-border/30"
              >
                Did Not Play
              </td>
            </tr>
          )}

          {/* DNP players */}
          {dnpPlayers.map((player) => (
            <PlayerRow key={player.playerId} player={player} showDNP={canShowDNP} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
